import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Avatar,
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverCloseButton,
    PopoverContent,
    PopoverHeader,
    PopoverTrigger,
    SimpleGrid,
    Stack,
    Table,
    Tbody,
    Td,
    Text,
    Textarea,
    Th,
    Thead,
    Tooltip,
    Tr,
    TableContainer,
    useColorMode,
    useDisclosure,
    useToast,
    VStack,
} from '@chakra-ui/react';
import NegocioModal from './NegocioModal';
import {
    AiOutlineSearch,
    AiOutlineDelete,
    AiOutlineEdit,
    AiOutlinePlus,
    AiOutlineEye,
    AiOutlineFilter,
    AiOutlinePartition,
    AiOutlineDollarCircle,
    AiOutlineTrophy,
    AiOutlineCloseCircle,
    AiOutlineUndo,
    AiOutlineUser,
    AiOutlineShop,
    AiOutlineCalendar,
    AiOutlineWarning,
} from 'react-icons/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { useComercial } from '../../../../contexts/ComercialContext';
import { usePermissions } from '../../../../contexts/AuthPermissionsContext';
import { useNogleTech } from '../../../../contexts/NogleTechContext';
import ConfirmDeleteDialog from '../../../../components/UI/ConfirmDeleteDialog';
import ConfirmActionDialog from '../../../../components/UI/ConfirmActionDialog';
import TablePagination from '../../../../components/UI/TablePagination';

const TIPO_META = {
    ganho:     { label: 'Ganho',     cor: '#01E975' },
    perdido:   { label: 'Perdido',   cor: '#FC8181' },
    aberto:    { label: 'Em aberto', cor: '#718096' },
    cancelado: { label: 'Cancelado', cor: '#FC8181' },
};
const PRIORIDADES = ['Alta', 'Média', 'Baixa'];

const FORM_INICIAL = {
    titulo: '', empresa: '', company_id: null, valor: '',
    pipelineId: '', colunaId: '', responsavel: '', assignee_id: null,
    canal: '', probabilidade: 50, prioridade: 'Média', dataFechamento: '', observacoes: '',
};

const fmtMoney = (v) =>
    `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtData = (d) => (!d ? '—' : new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('pt-BR'));
const parseObs = (v) => {
    if (!v) return '';
    if (typeof v === 'string' && (v.startsWith('{') || v.startsWith('['))) {
        try { return JSON.parse(v).observacoes || ''; } catch { return v; }
    }
    return v;
};

const Negocios = () => {
    const { colorMode } = useColorMode();
    const { pipelines, pipelineCards, addPipelineCard, updatePipelineCard, deletePipelineCard, atividades, resultadosNegocio } = useComercial();
    const { employees } = usePermissions();
    const { marcas } = useNogleTech();
    const toast = useToast();

    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

    const [busca, setBusca] = useState('');
    const [filterEmployeeIds, setFilterEmployeeIds] = useState([]);
    const [filterSearch, setFilterSearch] = useState('');
    const [filtroResultado, setFiltroResultado] = useState('todos');
    const [currentPage, setCurrentPage] = useState(1);
    // Paginação configurável compartilhada (TablePagination): 5 por padrão, opções 15/30/50
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const [editando, setEditando] = useState(null);
    const [visualizando, setVisualizando] = useState(null);
    const [excluindo, setExcluindo] = useState(null);
    // Confirmação de ações de 1 clique (cancelar/reativar) — evita cliques acidentais
    const [acaoConfirmar, setAcaoConfirmar] = useState(null); // { tipo: 'cancelar'|'reativar', card, fecharFicha }
    const [form, setForm] = useState(FORM_INICIAL);
    const [empresaFocada, setEmpresaFocada] = useState(false);
    const [responsavelFocado, setResponsavelFocado] = useState(false);

    // Etapa real do card (com tipo ganho/perdido/aberto)
    const etapaDeCard = useCallback((card) => {
        for (const pipe of pipelines || []) {
            const col = (pipe.colunas || []).find((c) => String(c.id) === String(card.colunaId));
            if (col) return { id: col.id, label: col.label || col.nome || 'Etapa', cor: col.cor || '#718096', tipo: col.tipo || 'aberto', funilNome: pipe.nome, semFunil: false };
        }
        return { id: 'sem', label: 'Sem funil', cor: '#FC8181', tipo: 'aberto', funilNome: 'Sem funil', semFunil: !!card.colunaId };
    }, [pipelines]);

    const resolverEmpresa = useCallback((card) => {
        const m = (marcas || []).find((x) => String(x.id) === String(card.company_id));
        return m?.nome || card.empresa || card.name_company || '';
    }, [marcas]);

    const resolverResponsavel = useCallback((card) => {
        const e = (employees || []).find((x) => String(x.id) === String(card.assignee_id));
        return e?.name || card.responsavel || '';
    }, [employees]);

    // Resultado efetivo do card: cancelado tem precedência sobre a etapa.
    // Classificação pelo PAPEL do resultado (1 positivo/ganho · 2 negativo/perdido) —
    // o nome do resultado é cosmético. Fallbacks: nome legado e tipo da coluna.
    const resultadoDeCard = useCallback((card) => {
        if (card.canceled) return 'cancelado';
        if (card.result_role === 1) return 'ganho';
        if (card.result_role === 2) return 'perdido';
        if (card.resultado && (card.result_role === 0 || card.result_role == null)) {
            const res = String(card.resultado).toLowerCase();
            if (res.includes('ganho')) return 'ganho';
            if (res.includes('perdido') || res.includes('perda')) return 'perdido';
        }
        if (card.completed) {
            const et = etapaDeCard(card);
            if (et.tipo === 'ganho' || et.tipo === 'perdido') return et.tipo;
        }
        return 'aberto';
    }, [etapaDeCard]);

    const negociosFiltrados = useMemo(() => {
        let lista = [...(pipelineCards || [])];
        if (filtroResultado !== 'todos') {
            lista = lista.filter((c) => resultadoDeCard(c) === filtroResultado);
        }
        if (filterEmployeeIds.length > 0) {
            lista = lista.filter((c) => filterEmployeeIds.includes(String(c.assignee_id)));
        }
        if (busca.trim()) {
            const q = busca.toLowerCase();
            lista = lista.filter((c) =>
                c.titulo?.toLowerCase().includes(q) ||
                resolverEmpresa(c).toLowerCase().includes(q) ||
                resolverResponsavel(c).toLowerCase().includes(q)
            );
        }
        return lista.sort((a, b) => (Number(b.valor) || 0) - (Number(a.valor) || 0));
    }, [pipelineCards, filtroResultado, filterEmployeeIds, busca, resultadoDeCard, resolverEmpresa, resolverResponsavel]);

    const totalPages = Math.max(1, Math.ceil(negociosFiltrados.length / itemsPerPage));
    const paginados = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return negociosFiltrados.slice(start, start + itemsPerPage);
    }, [negociosFiltrados, currentPage, itemsPerPage]);

    useEffect(() => { setCurrentPage(1); }, [busca, filtroResultado, filterEmployeeIds]);
    useEffect(() => { if (currentPage > totalPages) setCurrentPage(totalPages); }, [currentPage, totalPages]);

    const contadores = useMemo(() => {
        let aberto = 0, ganho = 0, perdido = 0, cancelado = 0;
        (pipelineCards || []).forEach((c) => {
            const t = resultadoDeCard(c);
            if (t === 'cancelado') cancelado++;
            else if (t === 'ganho') ganho++;
            else if (t === 'perdido') perdido++;
            else aberto++;
        });
        return { aberto, ganho, perdido, cancelado };
    }, [pipelineCards, resultadoDeCard]);

    // Funil selecionado no formulário (para listar as colunas/etapas)
    const funilDoForm = useMemo(
        () => (pipelines || []).find((p) => String(p.id) === String(form.pipelineId)),
        [pipelines, form.pipelineId]
    );

    const marcasSugeridas = useMemo(() => {
        const q = form.empresa?.trim().toLowerCase();
        if (!q) return [];
        return (marcas || []).filter((m) => m.nome?.toLowerCase().includes(q)).slice(0, 8);
    }, [marcas, form.empresa]);

    const employeesSugeridos = useMemo(() => {
        const q = form.responsavel?.trim().toLowerCase();
        if (!q) return [];
        return (employees || []).filter((e) => e.name?.toLowerCase().includes(q)).slice(0, 8);
    }, [employees, form.responsavel]);

    // -- Visualizar --
    const handleVisualizar = useCallback((card) => { setVisualizando(card); onViewOpen(); }, [onViewOpen]);
    const handleFecharVisualizar = useCallback(() => { setVisualizando(null); onViewClose(); }, [onViewClose]);

    // Deep-link: /comercial/negocios?openCardId=ID abre a ficha do negócio
    // (mesmo padrão do openActivityId de Atividades — usado pelo "Ver card" de Anotações)
    const location = useLocation();
    const navigate = useNavigate();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const id = params.get('openCardId');
        if (!id || !(pipelineCards || []).length) return;
        const card = pipelineCards.find((c) => String(c.id) === String(id));
        if (card) handleVisualizar(card);
        navigate('/comercial/negocios', { replace: true });
    }, [location.search, pipelineCards, handleVisualizar, navigate]);

    // -- Criar / Editar --
    const handleAbrir = useCallback((card = null) => {
        setEditando(card);
        if (card) {
            const et = etapaDeCard(card);
            setForm({
                titulo: card.titulo || '',
                empresa: resolverEmpresa(card),
                company_id: card.company_id || null,
                valor: card.valor || '',
                pipelineId: card.pipelineId || pipelines?.[0]?.id || '',
                colunaId: card.colunaId || '',
                responsavel: resolverResponsavel(card),
                assignee_id: card.assignee_id || null,
                canal: card.canal || '',
                probabilidade: card.probabilidade || 50,
                prioridade: card.prioridade || 'Média',
                dataFechamento: card.dataFechamento || '',
                observacoes: parseObs(card.observacoes),
            });
        } else {
            const pipe0 = pipelines?.[0];
            setForm({ ...FORM_INICIAL, pipelineId: pipe0?.id || '', colunaId: pipe0?.colunas?.[0]?.id || '' });
        }
        onOpen();
    }, [onOpen, etapaDeCard, resolverEmpresa, resolverResponsavel, pipelines]);

    const handleFechar = useCallback(() => {
        setEditando(null); setForm(FORM_INICIAL); setEmpresaFocada(false); setResponsavelFocado(false);
        onClose();
    }, [onClose]);

    const handleSalvar = useCallback(async () => {
        if (!form.titulo.trim()) { toast({ title: 'Título obrigatório', status: 'warning', duration: 2000 }); return; }
        if (!form.colunaId) { toast({ title: 'Selecione a etapa do funil', status: 'warning', duration: 2000 }); return; }
        const col = (funilDoForm?.colunas || []).find((c) => String(c.id) === String(form.colunaId));
        const completed = col ?col.tipo !== 'aberto' : false;
        const payload = {
            titulo: form.titulo,
            empresa: form.company_id ?null : form.empresa,
            company_id: form.company_id,
            valor: form.valor === '' ?0 : Number(form.valor),
            colunaId: form.colunaId,
            assignee_id: form.assignee_id,
            canal: form.canal,
            probabilidade: Number(form.probabilidade) || 0,
            prioridade: form.prioridade,
            dataFechamento: form.dataFechamento || null,
            observacoes: form.observacoes,
            completed,
        };
        try {
            if (editando) {
                await updatePipelineCard(editando.id, payload);
                toast({ title: 'Negócio atualizado', status: 'success', duration: 2000 });
            } else {
                await addPipelineCard(payload);
                toast({ title: 'Negócio criado', status: 'success', duration: 2000 });
            }
            handleFechar();
        } catch (err) {
            console.error('Erro ao salvar negócio:', err);
            toast({ title: 'Erro ao salvar', status: 'error', duration: 3500 });
        }
    }, [form, funilDoForm, editando, addPipelineCard, updatePipelineCard, handleFechar, toast]);

    // -- Excluir --
    const handleAbrirExcluir = useCallback((card) => { setExcluindo(card); onDelOpen(); }, [onDelOpen]);
    const handleConfirmarExcluir = useCallback(async () => {
        if (excluindo) {
            try {
                await deletePipelineCard(excluindo.id);
                toast({ title: 'Negócio removido', status: 'info', duration: 2000 });
            } catch (err) {
                console.error(err);
                toast({ title: 'Erro ao excluir', status: 'error', duration: 3000 });
            }
        }
        setExcluindo(null); onDelClose();
    }, [excluindo, deletePipelineCard, onDelClose, toast]);

    // Cancelar/Reativar: flag canceled no banco (mesma lógica da Pipeline; o card
    // não muda de coluna) — funciona em qualquer etapa, sem depender de coluna "Perdido".
    const handleCancelarNegocio = useCallback(async (card) => {
        if (!card) return;
        try {
            await updatePipelineCard(card.id, { canceled: 1 });
            toast({ title: 'Negócio cancelado', status: 'info', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao cancelar negócio', status: 'error', duration: 3000 });
        }
    }, [updatePipelineCard, toast]);

    const handleReativarNegocio = useCallback(async (card) => {
        if (!card) return;
        try {
            await updatePipelineCard(card.id, { canceled: 0 });
            toast({ title: 'Negócio reativado', status: 'success', duration: 2000 });
        } catch (err) {
            console.error(err);
            toast({ title: 'Erro ao reativar negócio', status: 'error', duration: 3000 });
        }
    }, [updatePipelineCard, toast]);

    // Executa a ação depois que o usuário confirmou no diálogo padrão
    const executarAcaoConfirmada = useCallback(async () => {
        if (!acaoConfirmar) return;
        const { tipo, card, fecharFicha } = acaoConfirmar;
        if (tipo === 'cancelar') await handleCancelarNegocio(card);
        else await handleReativarNegocio(card);
        if (fecharFicha) handleFecharVisualizar();
        setAcaoConfirmar(null);
    }, [acaoConfirmar, handleCancelarNegocio, handleReativarNegocio, handleFecharVisualizar]);

    const inputStyle = {
        bg: colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50',
        borderColor: 'borderPrimary', color: 'textPrimary',
        _focus: { borderColor: 'secondary', boxShadow: 'none' },
        _placeholder: { color: 'textSecondary' },
    };

    return (
        <VStack align="stretch" spacing={6}>
            {/* Header */}
            <Flex justify="space-between" align="center" wrap="wrap" gap={4} w="full">
                <Box flexShrink={0}>
                    <Heading size="lg" color="textPrimary" fontWeight="900">Negócios</Heading>
                </Box>
                <Flex gap={3} wrap="wrap" justify="flex-end" flex="1" minW="0" align="center">
                    <InputGroup w={{ base: 'full', sm: '240px' }} size="sm">
                        <InputLeftElement pointerEvents="none">
                            <Icon as={AiOutlineSearch} color="textSecondary" />
                        </InputLeftElement>
                        <Input
                            placeholder="Buscar negócio..."
                            value={busca}
                            onChange={(e) => setBusca(e.target.value)}
                            bg="surface" border="1px solid" borderColor="borderPrimary"
                            _focus={{ borderColor: 'secondary', boxShadow: 'none' }} color="textPrimary" borderRadius="xl"
                        />
                    </InputGroup>
                    <Popover placement="bottom-end" closeOnBlur>
                        <PopoverTrigger>
                            <Button size="sm" variant="outline" borderColor="borderPrimary" color="textPrimary"
                                leftIcon={<Icon as={AiOutlineFilter} boxSize={4} />} _hover={{ bg: 'surfaceHover' }} borderRadius="xl" fontWeight="800">
                                Filtros {filterEmployeeIds.length > 0 && `(${filterEmployeeIds.length})`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent bg="surface" borderColor="borderPrimary" color="textPrimary" w="320px" borderRadius="xl" _focus={{ boxShadow: 'none' }} zIndex={999}>
                            <PopoverArrow bg="surface" />
                            <PopoverHeader borderColor="borderPrimary" fontWeight="bold" fontSize="sm">Filtrar Responsáveis</PopoverHeader>
                            <PopoverCloseButton />
                            <PopoverBody maxH="400px" overflowY="auto" p={0}>
                                <Box p={3} borderBottom="1px solid" borderColor="borderPrimary">
                                    <Input placeholder="Buscar..." size="sm" bg={colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50'}
                                        border="1px solid" borderColor="borderPrimary" value={filterSearch}
                                        onChange={(e) => setFilterSearch(e.target.value)} mb={2} borderRadius="md" _focus={{ borderColor: 'secondary', boxShadow: 'none' }} />
                                    <Button size="xs" variant="ghost" colorScheme="green" w="100%" onClick={() => setFilterEmployeeIds([])} isDisabled={filterEmployeeIds.length === 0}>
                                        Limpar Filtros
                                    </Button>
                                </Box>
                                <Stack spacing={0}>
                                    {employees?.filter((emp) => !filterSearch || emp.name?.toLowerCase().includes(filterSearch.toLowerCase())).map((emp) => {
                                        const isChecked = filterEmployeeIds.includes(String(emp.id));
                                        return (
                                            <Box key={emp.id} px={4} py={2} cursor="pointer" _hover={{ bg: 'surfaceHover' }} display="flex" alignItems="center" gap={3}
                                                onClick={() => setFilterEmployeeIds((prev) => {
                                                    const sid = String(emp.id);
                                                    return prev.includes(sid) ?prev.filter((x) => x !== sid) : [...prev, sid];
                                                })}>
                                                <Checkbox isChecked={isChecked} colorScheme="green" borderColor="borderPrimary" pointerEvents="none" />
                                                <Avatar size="xs" name={emp.name} />
                                                <Text fontSize="sm" noOfLines={1}>{emp.name}</Text>
                                            </Box>
                                        );
                                    })}
                                    {(!employees || employees.length === 0) && (
                                        <Box p={4} textAlign="center" color="textMuted" fontSize="sm">Nenhum responsável encontrado.</Box>
                                    )}
                                </Stack>
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                    <Button leftIcon={<AiOutlinePlus />} size="sm" flexShrink={0} bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" onClick={() => handleAbrir()}>
                        Novo Negócio
                    </Button>
                </Flex>
            </Flex>

            {/* Filtros por resultado (filtro específico desta tela) */}
            <Flex gap={2} wrap="wrap">
                {[
                    { id: 'todos',     label: 'Todos',      count: (pipelineCards || []).length },
                    { id: 'aberto',    label: 'Em aberto',  count: contadores.aberto },
                    { id: 'ganho',     label: 'Ganhos',     count: contadores.ganho },
                    { id: 'perdido',   label: 'Perdidos',   count: contadores.perdido },
                    { id: 'cancelado', label: 'Cancelados', count: contadores.cancelado },
                ].map((f) => {
                    const active = filtroResultado === f.id;
                    return (
                        <Button key={f.id} size="sm" variant="ghost"
                            border="1px solid" borderColor={active ?'rgba(1,233,117,0.45)' : 'borderSubtle'} bg={active ?'secondarySubtle' : 'transparent'} color={active ?'secondary' : 'textSecondary'}
                            _hover={{ bg: active ?'secondarySubtle' : 'surfaceHover', color: active ?'secondary' : 'textPrimary' }}
                            borderRadius="lg" fontWeight="700" onClick={() => setFiltroResultado(f.id)}
                            rightIcon={<Badge bg={active ?'secondary' : 'surfaceRaised'} color={active ?'primary' : 'textSecondary'} fontSize="xs" fontWeight="800" borderRadius="full" minW="20px" textAlign="center">{f.count}</Badge>}>
                            {f.label}
                        </Button>
                    );
                })}
            </Flex>

            {/* Tabela */}
            <Box bg="surface" border="1px solid" borderColor="borderPrimary" borderRadius="xl" overflow="hidden">
                <Flex align="center" gap={3} p={4} bg={colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50'} borderBottom="1px solid" borderColor="borderPrimary">
                    <Icon as={AiOutlineDollarCircle} color="secondary" boxSize="20px" />
                    <Heading size="sm" color="textPrimary" textTransform="uppercase" letterSpacing="1px" fontWeight="800">Lista de Negócios</Heading>
                    <Badge ml="auto" bg="rgba(2,234,117,0.15)" color="secondary" fontWeight="800">{negociosFiltrados.length}</Badge>
                </Flex>
                <TableContainer>
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Resultado</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Negócio</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Funil</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Etapa</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Responsável</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Valor</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px">Fechamento</Th>
                                <Th color="textSecondary" borderColor="borderPrimary" fontSize="10px" letterSpacing="1px" isNumeric>Ações</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {negociosFiltrados.length === 0 && (
                                <Tr><Td colSpan={8} textAlign="center" py={10} color="textSecondary" borderColor="borderPrimary">Nenhum negócio encontrado</Td></Tr>
                            )}
                            {paginados.map((card) => {
                                const et = etapaDeCard(card);
                                let outcomeLabel = '';
                                let outcomeColor = '';
                                
                                if (card.canceled) {
                                    outcomeLabel = 'Cancelado';
                                    outcomeColor = '#FC8181';
                                } else if (card.resultado) {
                                    outcomeLabel = card.resultado;
                                    const matchingOption = (resultadosNegocio || []).find(r => String(r.id) === String(card.result_id));
                                    outcomeColor = matchingOption?.cor || '#718096';
                                } else {
                                    const meta = TIPO_META[et.tipo] || TIPO_META.aberto;
                                    outcomeLabel = meta.label;
                                    outcomeColor = meta.cor;
                                }
                                return (
                                    <Tr key={card.id} _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50' }} cursor="pointer" onClick={() => handleVisualizar(card)} opacity={card.canceled ? 0.6 : 1}>
                                        <Td borderColor="borderPrimary">
                                            <Badge bg={`${outcomeColor}20`} color={outcomeColor} fontSize="xs" fontWeight="700" borderRadius="md" px={2}>{outcomeLabel}</Badge>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            <VStack align="start" spacing={0.5}>
                                                <Text fontSize="sm" fontWeight="700" color="textPrimary" noOfLines={1} maxW="240px">{card.titulo || '—'}</Text>
                                                {resolverEmpresa(card) && <Text fontSize="10px" color="textSecondary" fontWeight="600" noOfLines={1} maxW="240px">{resolverEmpresa(card)}</Text>}
                                            </VStack>
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            {et.semFunil ?(
                                                <Badge bg="rgba(252,129,129,0.16)" color="#FC8181" border="1px solid" borderColor="rgba(252,129,129,0.35)" fontSize="8px" fontWeight="900" borderRadius="md">
                                                    <HStack spacing={1}>
                                                        <Icon as={AiOutlineWarning} boxSize="9px" />
                                                        <Text>Sem funil</Text>
                                                    </HStack>
                                                </Badge>
                                            ) : (
                                                <Text fontSize="xs" color="textPrimary" noOfLines={1}>{et.funilNome}</Text>
                                            )}
                                        </Td>
                                        <Td borderColor="borderPrimary">
                                            <HStack spacing={2}>
                                                <Box w="8px" h="8px" borderRadius="full" bg={et.cor} flexShrink={0} />
                                                <Text fontSize="xs" color="textPrimary" noOfLines={1}>{et.label}</Text>
                                            </HStack>
                                        </Td>
                                        <Td borderColor="borderPrimary"><Text fontSize="xs" color="textPrimary" fontWeight="600">{resolverResponsavel(card) || '—'}</Text></Td>
                                        <Td borderColor="borderPrimary" isNumeric><Text fontSize="sm" fontWeight="800" color={et.tipo === 'ganho' ?'secondary' : 'textPrimary'}>{fmtMoney(card.valor)}</Text></Td>
                                        <Td borderColor="borderPrimary"><Text fontSize="xs" color="textSecondary">{fmtData(card.dataFechamento)}</Text></Td>
                                        <Td borderColor="borderPrimary" isNumeric>
                                            <HStack spacing={1} justify="flex-end" onClick={(e) => e.stopPropagation()}>
                                                <Tooltip label="Visualizar" fontSize="xs">
                                                    <IconButton aria-label="Visualizar" icon={<AiOutlineEye />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: 'secondary', bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }} onClick={() => handleVisualizar(card)} />
                                                </Tooltip>
                                                <Tooltip label="Editar" fontSize="xs">
                                                    <IconButton aria-label="Editar" icon={<AiOutlineEdit />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: 'secondary', bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }} onClick={() => handleAbrir(card)} />
                                                </Tooltip>
                                                {card.canceled ? (
                                                    <Tooltip label="Reativar" fontSize="xs">
                                                        <IconButton aria-label="Reativar" icon={<AiOutlineUndo />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: '#ECC94B', bg: 'rgba(236,201,75,0.12)' }} onClick={() => setAcaoConfirmar({ tipo: 'reativar', card })} />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip label="Cancelar" fontSize="xs">
                                                        <IconButton aria-label="Cancelar" icon={<AiOutlineCloseCircle />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: '#FC8181', bg: 'rgba(252,129,129,0.12)' }} onClick={() => setAcaoConfirmar({ tipo: 'cancelar', card })} />
                                                    </Tooltip>
                                                )}
                                                <Tooltip label="Excluir" fontSize="xs">
                                                    <IconButton aria-label="Excluir" icon={<AiOutlineDelete />} size="xs" variant="ghost" color="textSecondary" _hover={{ color: 'red.400', bg: 'rgba(229,62,62,0.1)' }} onClick={() => handleAbrirExcluir(card)} />
                                                </Tooltip>
                                            </HStack>
                                        </Td>
                                    </Tr>
                                );
                            })}
                        </Tbody>
                    </Table>
                </TableContainer>

                {/* Paginação configurável compartilhada (5/15/30/50) */}
                <TablePagination
                    page={currentPage}
                    pageSize={itemsPerPage}
                    totalItems={negociosFiltrados.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(n) => { setItemsPerPage(n); setCurrentPage(1); }}
                />
            </Box>

            {/* -- Modal Visualizar -- */}
            <Modal isOpen={isViewOpen} onClose={handleFecharVisualizar} isCentered size="lg" scrollBehavior="inside">
                <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
                <ModalContent bg="surface" border="1px solid" borderColor="borderPrimary" color="textPrimary" borderRadius="2xl" overflow="hidden">
                    <ModalCloseButton color="textSecondary" zIndex={2} />
                    <ModalBody pt={0} pb={2} px={6}>
                        {visualizando && (() => {
                            const et = etapaDeCard(visualizando);
                            const meta = TIPO_META[resultadoDeCard(visualizando)] || TIPO_META.aberto;
                            return (
                                <VStack spacing={5} align="stretch">
                                    <Box mx={-6} pt={10} pb={5} px={6} bgGradient={`linear(to-b, ${et.cor}26, transparent)`}>
                                        <Flex direction="column" align="center" gap={3}>
                                            <Flex boxSize="60px" borderRadius="2xl" align="center" justify="center" bg={`${et.cor}1A`} border="1px solid" borderColor={`${et.cor}40`}>
                                                <Icon as={AiOutlinePartition} color={et.cor} boxSize="28px" />
                                            </Flex>
                                            <VStack spacing={2}>
                                                <Text fontSize="xl" fontWeight="900" textAlign="center" lineHeight="1.2">{visualizando.titulo || 'Negócio'}</Text>
                                                <HStack spacing={2} flexWrap="wrap" justify="center">
                                                    <Badge bg={`${et.cor}22`} color={et.cor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>Etapa: {et.label}</Badge>
                                                    <Badge bg={`${meta.cor}22`} color={meta.cor} fontSize="xs" fontWeight="800" borderRadius="full" px={2.5} py={0.5}>Resultado: {meta.label}</Badge>
                                                    {et.semFunil && (
                                                        <Badge bg="rgba(252,129,129,0.16)" color="#FC8181" fontSize="xs" fontWeight="900" borderRadius="full" px={2.5} py={0.5}>
                                                            Sem funil
                                                        </Badge>
                                                    )}
                                                </HStack>
                                            </VStack>
                                        </Flex>
                                    </Box>
                                    <SimpleGrid columns={2} spacing={3}>
                                        <DetField icon={AiOutlineDollarCircle} label="Valor" accent="#01E975" value={fmtMoney(visualizando.valor)} colorMode={colorMode} />
                                        <DetField icon={AiOutlinePartition} label="Funil" accent={et.cor} value={et.funilNome} colorMode={colorMode} />
                                        <DetField icon={AiOutlineShop} label="Empresa / Marca" accent="#ED8936" value={resolverEmpresa(visualizando)} colorMode={colorMode} />
                                        <DetField icon={AiOutlineUser} label="Responsável" accent="#9F7AEA" value={resolverResponsavel(visualizando)} colorMode={colorMode} />
                                        <DetField icon={AiOutlinePartition} label="Canal" accent="#4299E1" value={visualizando.canal} colorMode={colorMode} />
                                        <DetField icon={AiOutlineTrophy} label="Probabilidade" accent="#ECC94B" value={visualizando.probabilidade != null ?`${visualizando.probabilidade}%` : '—'} colorMode={colorMode} />
                                        <DetField icon={AiOutlineCalendar} label="Fechamento" accent="#FC8181" value={fmtData(visualizando.dataFechamento)} colorMode={colorMode} />
                                        <DetField icon={AiOutlineWarning} label="Prioridade" accent="#ED8936" value={visualizando.prioridade} colorMode={colorMode} />
                                        {parseObs(visualizando.observacoes) && <DetField label="Observações" value={parseObs(visualizando.observacoes)} span colorMode={colorMode} />}
                                    </SimpleGrid>
                                </VStack>
                            );
                        })()}
                    </ModalBody>
                    <ModalFooter borderTop="1px solid" borderColor="borderPrimary" mt={2} gap={3}>
                        <Button leftIcon={<AiOutlineDelete />} variant="ghost" color="red.400" _hover={{ bg: 'rgba(229,62,62,0.1)' }} size="sm"
                            onClick={() => { handleFecharVisualizar(); handleAbrirExcluir(visualizando); }}>Excluir</Button>
                        <Box flex={1} />
                        <Button variant="ghost" color="textSecondary" _hover={{ bg: colorMode === 'dark' ?'whiteAlpha.100' : 'blackAlpha.100' }} onClick={handleFecharVisualizar} size="sm">Fechar</Button>
                        {visualizando?.canceled ? (
                            <Button leftIcon={<AiOutlineUndo />} variant="outline" borderColor="rgba(236,201,75,0.45)" color="#ECC94B" _hover={{ bg: 'rgba(236,201,75,0.12)' }} fontWeight="800" size="sm"
                                onClick={() => setAcaoConfirmar({ tipo: 'reativar', card: visualizando, fecharFicha: true })}>Reativar</Button>
                        ) : (
                            <Button leftIcon={<AiOutlineCloseCircle />} variant="outline" borderColor="rgba(252,129,129,0.45)" color="#FC8181" _hover={{ bg: 'rgba(252,129,129,0.12)' }} fontWeight="800" size="sm"
                                onClick={() => setAcaoConfirmar({ tipo: 'cancelar', card: visualizando, fecharFicha: true })}>Cancelar</Button>
                        )}
                        <Button leftIcon={<AiOutlineEdit />} bg="secondary" color="primary" _hover={{ bg: 'secondaryHover' }} fontWeight="800" size="sm"
                            onClick={() => { handleFecharVisualizar(); handleAbrir(visualizando); }}>Editar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* -- Modal Criar/Editar -- */}
            <NegocioModal
                isOpen={isOpen}
                onClose={handleFechar}
                cardEditando={editando}
                pipelineAtivo={funilDoForm || pipelines?.[0]}
                defaultColunaId={form.colunaId || pipelines?.[0]?.colunas?.[0]?.id}
            />

            {/* -- Confirmação padrão de exclusão -- */}
            <ConfirmDeleteDialog
                isOpen={isDelOpen}
                onClose={onDelClose}
                onConfirm={handleConfirmarExcluir}
                title="Excluir Negócio"
                itemName={excluindo?.titulo}
                warning={excluindo && (atividades || []).some(a => String(a.cardId) === String(excluindo.id))}
                description={(excluindo && (atividades || []).some(a => String(a.cardId) === String(excluindo.id)))
                    ? "Atenção: Este negócio possui atividades vinculadas. Se você prosseguir com a exclusão, essas atividades perderão o vínculo com este negócio."
                    : ""}
            />

            {/* -- Confirmação padrão de ação (cancelar/reativar) — evita cliques acidentais -- */}
            <ConfirmActionDialog
                isOpen={!!acaoConfirmar}
                onClose={() => setAcaoConfirmar(null)}
                onConfirm={executarAcaoConfirmada}
                title={acaoConfirmar?.tipo === 'cancelar' ? 'Cancelar Negócio' : 'Reativar Negócio'}
                itemName={acaoConfirmar?.card?.titulo}
                description={acaoConfirmar?.tipo === 'cancelar'
                    ? 'O negócio ficará marcado como CANCELADO e sai dos KPIs de abertos. É possível reativá-lo depois.'
                    : 'O negócio volta a contar como em andamento.'}
                confirmLabel={acaoConfirmar?.tipo === 'cancelar' ? 'Sim, cancelar' : 'Sim, reativar'}
                scheme={acaoConfirmar?.tipo === 'cancelar' ? 'red' : 'amber'}
                icon={acaoConfirmar?.tipo === 'cancelar' ? AiOutlineCloseCircle : AiOutlineUndo}
            />
        </VStack>
    );
};

const DetField = ({ icon, label, value, accent = '#718096', span, colorMode }) => (
    <Box bg={colorMode === 'dark' ?'whiteAlpha.50' : 'blackAlpha.50'} border="1px solid" borderColor="borderPrimary" borderRadius="xl" p={3} gridColumn={span ?{ md: 'span 2' } : undefined}>
        <HStack spacing={2} mb={1}>
            {icon && <Icon as={icon} color={accent} boxSize="12px" />}
            <Text fontSize="9px" color="textSecondary" fontWeight="900" textTransform="uppercase" letterSpacing="1px">{label}</Text>
        </HStack>
        <Text fontSize="sm" color="textPrimary" fontWeight="700" lineHeight="1.4" whiteSpace="pre-wrap">{value || '—'}</Text>
    </Box>
);

export default Negocios;
